import { useTranslation } from 'next-i18next'

import { RegisterErrors, SignUpScenario } from '~/types/sign-up'
import { cleanNullAndUndefined, validateEmail } from '~/utils'
import useCurrentUser from '~/hooks/useCurrentUser'

import useRegisterContext from './useRegisterContext'

export default function useValidateRegisterFields(): () => RegisterErrors {
  const { t } = useTranslation('validation')
  const { fields, signUpScenario } = useRegisterContext()
  const user = useCurrentUser()

  const sharedFields = {
    country: fields.country,
  }

  const requiredUserFields = {
    ...sharedFields,
    ...{ email: fields.email },
  }

  const requiredNewUserFields = {
    ...requiredUserFields,
    password: fields.password,
    passwordConfirm: fields.passwordConfirm,
  }

  const requiredCheckoutForSelfFields = {
    ...sharedFields,
    fullName: fields.fullName,
    phone: fields.phone,
    skill: fields.skill,
    timezone: fields.timezone,
    yearsInSchool: fields.yearsInSchool,
  }

  const requiredCheckoutForOtherFields = {
    ...sharedFields,
    fullName: fields.fullName,
    phone: fields.phone,
    timezone: fields.timezone,
  }

  const completeProfileFields = {
    gradeLevelId: fields.gradeLevelId,
    skill: fields.skill,
    type: fields.userType,
    yearsInSchool: fields.yearsInSchool,
  }

  return function validateRegisterFields(): RegisterErrors {
    const isCheckoutForSelf = signUpScenario === SignUpScenario.CHECKOUT_FOR_SELF
    const isCheckoutForOther = signUpScenario === SignUpScenario.CHECKOUT_FOR_OTHER
    const isCompleteProfileScenario = signUpScenario === SignUpScenario.COMPLETE_PROFILE
    const isSubscriptionScenario = signUpScenario === SignUpScenario.SUBSCRIPTION
    const isMissingRequiredFieldsScenario = signUpScenario === SignUpScenario.MISSING_REQUIRED_FIELDS

    const shouldValidateNewUser = !user

    const fieldsToValidate = {
      ...(shouldValidateNewUser ? requiredNewUserFields : undefined),
      ...(isMissingRequiredFieldsScenario ? requiredUserFields : undefined),
      ...(isCheckoutForSelf ? requiredCheckoutForSelfFields : undefined),
      ...(isCheckoutForOther ? requiredCheckoutForOtherFields : undefined),
      ...(isCompleteProfileScenario ? completeProfileFields : undefined),
    }

    if (isSubscriptionScenario) {
      delete fieldsToValidate.country
    }

    const missingErrors = Object.entries(fieldsToValidate).reduce((acc, [key, value]) => {
      if (typeof value === 'number' && value > -1) {
        return acc
      }

      if (typeof value === 'string' && value.trim().length > 0) {
        return acc
      }

      return {
        ...acc,
        [key]: t('fieldRequired'),
      }
    }, {})

    const passwordMismatchError =
      requiredNewUserFields.password !== requiredNewUserFields.passwordConfirm ? t('passwordMatch') : undefined

    const passwordLengthError =
      requiredNewUserFields.password && requiredNewUserFields.password.length < 8 ? t('passwordLength') : undefined

    const emailError =
      requiredNewUserFields.email != null && !validateEmail(requiredNewUserFields.email) ? t('validEmail') : undefined

    return cleanNullAndUndefined({
      email: emailError,
      password: passwordLengthError,
      passwordConfirm: passwordMismatchError,
      ...missingErrors,
    })
  }
}
